import React, { Fragment, useState, useRef, useEffect } from "react";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import { usePopper } from "react-popper";
import { bottom, left, right, top } from "@popperjs/core";
import { classNames } from "../../helpers/classNames";
import Search from "./Search";
import Image from "../elements/Image";
import NoData from "../../assets/images/no-results-bg.2d2c6ee3.png";
import CompressImage from "../elements/CompressedImage";
import { elementResize } from "../../helpers";

const Select = ({
  isSearch = false,
  xPlacement,
  dropdownData = [],
  dropdownClass,
  dropdownButtonClass,
  optionsClass,
  selectedValue,
  setSelectedValue = () => {},
  onSearchHandler = () => {},
  onScroll = () => {},
  loading,
  label,
  labelClasses,
  errorType,
  placeholder,
  errorText,
  required,
  isDisabled = false,
  ...props
}) => {
  const divRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const [list, setList] = useState([]);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    elementResize(divRef, (val) => {
      setWidth(val?.width);
    });
  }, []);

  useEffect(() => {
    if (
      dropdownData &&
      Array.isArray(dropdownData) &&
      dropdownData?.length > 0
    ) {
      setList(
        dropdownData?.filter((item) => item?._id || item?.value || item?.name)
      );
    } else {
      setList([]);
    }
  }, [JSON.stringify(dropdownData)]);

  useEffect(() => {
    if (
      selectedValue !== undefined &&
      dropdownData &&
      Array.isArray(dropdownData) &&
      dropdownData?.length > 0
    ) {
      setSelected(
        dropdownData.find(
          (item) => item?.value === selectedValue || item?._id === selectedValue
        ) || null
      );
    } else {
      setSelected(null);
    }
  }, [JSON.stringify({ selectedValue, dropdownData })]);

  return (
    <div className="relative w-full">
      {label && (
        <div
          className={classNames(
            "text-xs font-medium text-shades-07 mb-1 font-CanelaDeck",
            labelClasses
          )}
        >
          {label}
          {required && (
            <span className="text-[8px] text-red-500 ml-1 absolute top-[-2px]">
              <i className="fa-solid fa-asterisk"></i>
            </span>
          )}
        </div>
      )}
      <Listbox
        as={"div"}
        className="relative"
        value={selected || ""}
        onChange={(value) => {
          setSelected(value);
          setSelectedValue(value?.value, value);
        }}
        disabled={isDisabled}
      >
        <ListboxButton ref={divRef} className="flex items-center w-full">
          <div
            className={classNames(
              "relative w-full h-10 flex items-center gap-2 pr-2 pl-3 rounded-md bg-white border  px-3 font-CanelaDeck text-sm text-gable-green !ring-0 !outline-0 focus:border-shades-07 transition-all duration-200 ",
              dropdownButtonClass
              // open ? "border-shades-07" : "border-shades-05/50"
            )}
          >
            {selected?.image && (
              <div className="w-5 aspect-1 overflow-hidden rounded-md flex-shrink-0">
                <CompressImage
                  src={selected?.image}
                  alt={selected?.image}
                  quality={0.2}
                  className={"w-full h-full object-cover object-center"}
                />
              </div>
            )}
            <div className="font-CanelaDeck whitespace-nowrap truncate">
              {selected?.name || (
                <span className="text-shades-07">{placeholder}</span>
              )}
            </div>
            <div className="text-[10px] text-slate-400 ml-auto">
              {loading ? (
                <i
                  className={"fa-duotone fa-spinner-third animate-spin text-sm"}
                />
              ) : (
                <i className={"fa-light fa-fw fa-chevron-down"}></i>
              )}
            </div>
          </div>
        </ListboxButton>
        <ListboxOptions
          anchor="bottom"
          transition
          className={classNames(
            "mt-1 origin-top-right bg-white border-0 divide-y divide-slate-100 rounded-md shadow ring-0 overflow-auto scroll-smooth scrollbar",
            dropdownClass
          )}
          style={{ minWidth: width }}
        >
          {isSearch && (
            <div className="py-2 px-3">
              <Search
                placeholder={"Search"}
                iconClasses={""}
                search={(e) => onSearchHandler(e)}
              />
            </div>
          )}
          {Array.isArray(list) && !loading && list?.length > 0 ? (
            <div
              className="!max-h-96 overflow-auto scroll-smooth scrollbar"
              onScroll={onScroll}
            >
              {list.map((item, index) => (
                <ListboxOption
                  key={index}
                  className={() =>
                    classNames(
                      "relative py-2 pl-8 pr-2 text-xs cursor-pointer transition-all duration-200",
                      optionsClass,
                      selected?.value === item?.value
                        ? "bg-chestnut-rose text-white"
                        : "text-shades-07 hover:bg-chestnut-rose hover:text-white"
                    )
                  }
                  value={item}
                >
                  <>
                    <div
                      className={classNames(
                        "truncate font-CanelaDeck flex items-center gap-2",
                        selected?.value === item?.value
                          ? "font-medium"
                          : "font-normal"
                      )}
                    >
                      {item?.image && (
                        <div className="w-5 aspect-1 overflow-hidden rounded-md flex-shrink-0">
                          <Image
                            src={item.image}
                            alt={item.image}
                            className={
                              "w-full h-full object-cover object-center"
                            }
                          />
                        </div>
                      )}
                      {item?.name}
                    </div>
                    {selected?.value === item?.value && (
                      <div className="absolute inset-y-0 left-0 flex items-center pl-2 text-base">
                        <i className="fa-regular fa-fw fa-check"></i>
                      </div>
                    )}
                  </>
                </ListboxOption>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center w-full py-3 space-y-3">
              <div className={classNames("relative overflow-hidden w-32 h-32")}>
                <Image src={NoData} alt="no-data" className="w-full h-full" />
              </div>
            </div>
          )}
        </ListboxOptions>
      </Listbox>
      {errorType && errorType === "error" && (
        <div className="text-xs text-red-600 mt-1">
          <i className="fa-regular fa-fw fa-square-exclamation text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "warning" && (
        <div className="text-xs text-amber-600 mt-1">
          <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "success" && (
        <div className="text-xs text-green-600 mt-1">
          <i className="fa-regular fa-fw fa-circle-check text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "info" && (
        <div className="text-xs text-sky-600 mt-1">
          <i className="fa-regular fa-fw fa-circle-info text-sm mr-1"></i>
          {errorText}
        </div>
      )}
    </div>
  );
};

export default Select;
